import React, { useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'

import { Check } from '@aok/icon-linear'
import { PrimaryButton, SecondaryButton, Textarea } from '@aok/ui-elements'

import { APIContext } from '../../../context/APIContext'

import { handleUniqueHash, handlePostFeedback } from '../../../utils/requests/handleFeedbackFormRequest'
import { getFeedbackCookie, setFeedbackCookie } from '../../../utils/feedbackCookie'
import { getLocalisationCookie } from '../../../utils/LocalisationCookie'

import MacroContainer from '../../_layout/Macro/MacroContainer'

const FeedbackForm = ({
  campaign,
  client,
  form,
  textfieldNameYes,
  textfieldNameNo,
  buttonNameYes,
  buttonNameNo,
  placeholderYes,
  placeholderNo,
}) => {
  const [textFeedback, setTextFeedback] = useState(null) // textual feedback from textarea
  const [isArticleHelpful, setIsArticleHelpful] = useState(null) // clicked button state

  const [formRequestPending, setFormRequestPending] = useState(false)
  const [hashRequestPending, setHashRequestPending] = useState(false)

  const [buttonClicked, setButtonClicked] = useState(null)
  const [showCompletionMsg, setShowCompletionMsg] = useState(false)

  const [hasErrors, setHasErrors] = useState(false)
  const [uniqueHash, setUniqueHash] = useState(null)
  const [clientId, setClientId] = useState(null)
  const [showErrorMsg, setShowErrorMsg] = useState(false)
  const apiContext = useContext(APIContext)

  const [defaultErrorMsg] = useState(
    'Es gab einen Fehler und Ihr Feedback konnte leider nicht gesendet werden. Bitte laden Sie die Webseite neu und probieren es erneut.'
  )

  useEffect(() => {
    if (getFeedbackCookie()) setShowCompletionMsg(true)

    // retrieve client from cookie by default or from API as fallback
    const localisationCookie = getLocalisationCookie()
    if (localisationCookie) {
      setClientId(localisationCookie.client.id)
    } else {
      setClientId(client)
    }
    return () => {}
  }, [client])

  // retrieving unique hash on yes/no button click
  const onButtonClick = (isHelpful) => {
    setIsArticleHelpful(isHelpful)
    setButtonClicked(true)

    if (!uniqueHash && !hasErrors) {
      setHashRequestPending(true)
      handleUniqueHash(
        apiContext,
        (data) => {
          setUniqueHash(data.unique_hash.hash)
          setHashRequestPending(false)
        },
        () => {
          setHasErrors(true)
          setShowErrorMsg(true)
          setHashRequestPending(false)
        }
      )
    } else if (hasErrors) {
      setShowErrorMsg(true)
    }
  }

  const onSubmitFeedback = () => {
    if (!uniqueHash) {
      setShowErrorMsg(true)
      return
    }

    // select right keys for text field and button depending on selected yes/no button
    let textfieldName, buttonName
    if (isArticleHelpful) {
      textfieldName = textfieldNameYes
      buttonName = buttonNameYes
    } else {
      textfieldName = textfieldNameNo
      buttonName = buttonNameNo
    }

    let requestBody = {
      params: JSON.stringify({
        [textfieldName]: textFeedback,
        [buttonName]: true,
      }),
      download_url: window.location.href,
      confirm_url: window.location.href,
      form_url: window.location.href,
      client: clientId,
      unique_hash: uniqueHash,
      form,
      campaign,
    }

    setFormRequestPending(true)
    handlePostFeedback(
      apiContext,
      requestBody,
      () => {
        setShowCompletionMsg(true)
        setFeedbackCookie()
        setFormRequestPending(false)
      },
      () => {
        setHasErrors(true)
        setShowErrorMsg(true)
        setFormRequestPending(false)
      }
    )
  }

  return (
    <MacroContainer dataTrackingName="feedback-form">
      {!showCompletionMsg ? (
        <>
          {/* First Step: yes/no question */}
          <div className="flex flex-col gap-20 600:flex-row 600:items-center 600:justify-between">
            <p
              className="
                    font-buenos-aires font-semibold text-005e3f
                    text-28 leading-36
                    1280:text-32 1280:leading-44
                  "
            >
              Waren diese Informationen hilfreich für Sie?
            </p>

            {/* Yes/No Buttons */}
            <div className="flex flex-col relative 600:flex-row 600:items-end">
              <SecondaryButton className="600:w-96 1280:w-112" onClick={() => onButtonClick(true)}>
                Ja
              </SecondaryButton>
              <SecondaryButton
                className="600:w-96 1280:w-112 mt-16 600:mt-0 600:ml-16"
                onClick={() => onButtonClick(false)}
              >
                Nein
              </SecondaryButton>
            </div>
          </div>

          {/* Second Step: textual feedback */}
          {buttonClicked && (
            <div className="mt-32 600:mt-24 1000:mt-32">
              {/* feedback form element */}
              <div className="relative flex flex-col gap-16">
                <p className="text-20 leading-28 font-semibold font-buenos-aires">Ihre Meinung</p>
                <div className="relative w-full">
                  <Textarea
                    variant="light"
                    errorMessage={showErrorMsg ? defaultErrorMsg : null}
                    label="Ihre Meinung"
                    labelVisibility="hide"
                    textareaProps={{
                      'placeholder': `${isArticleHelpful ? placeholderYes : placeholderNo}`,
                      'id': 'text-feedback',
                      'name': 'text-feedback',
                      'rows': 4,
                      'data-is-helpful': isArticleHelpful ? 'yes' : 'no',
                    }}
                    textareaClassName="h-188 600:h-140 840:h-auto"
                    onChange={(event) => setTextFeedback(event.target.value)}
                    onFocus={() => setShowErrorMsg(false)}
                  />
                </div>
              </div>

              {/* Send feedback button */}
              <PrimaryButton
                className="h-48 w-full mt-32 600:w-auto 600:ml-auto 600:mt-32 1000:mt-40"
                isDisabled={formRequestPending || hashRequestPending}
                onClick={onSubmitFeedback}
              >
                {formRequestPending ? 'Bitte warten...' : 'Feedback senden'}
              </PrimaryButton>
            </div>
          )}
        </>
      ) : (
        <div className="flex items-start justify-start gap-16 1000:items-center">
          <Check className="w-32 h-32 600:w-40 600:h-40 text-005e3f fill-current shrink-0" />
          <p
            className="
                font-buenos-aires font-semibold text-005e3f
                text-28 leading-36 text-center
                1280:text-32 1280:leading-44
              "
          >
            Vielen Dank für ihr Feedback!
          </p>
        </div>
      )}
    </MacroContainer>
  )
}

FeedbackForm.propTypes = {
  campaign: PropTypes.number,
  client: PropTypes.number,
  form: PropTypes.number,
  slug: PropTypes.string,
  textfieldNameYes: PropTypes.string,
  textfieldNameNo: PropTypes.string,
  placeholderYes: PropTypes.string,
  placeholderNo: PropTypes.string,
}

FeedbackForm.defaultProps = {
  placeholderYes: 'Was hat ihnen besonders gefallen?',
  placeholderNo: 'Was können wir besser machen?',
}

export default FeedbackForm
